<div class="p-4">
  <div class="block sm:inline-block w-full sm:w-auto">
    <div class="w-full sm:max-w-3xl">

      <!-- Instructions -->
      <div class="mb-4 text-sm text-gray-500 flex flex-col gap-1 max-w-xl">
        <span>Sends a re-engagement text to consumer referrals created within the date range below.</span>
        <span><strong class="text-agentium_gray-text">Preview</strong> shows how many referrals would be messaged — no texts are sent. <strong class="text-agentium_gray-text">Send</strong> asks you to confirm the count, then sends for real.</span>
      </div>

      @if (campaignDisabled) {
        <div class="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 max-w-xl">
          This campaign is temporarily disabled.
        </div>
      }

      <!-- Controls -->
      <div class="rounded-lg border border-gray-200 bg-white p-5 mb-4 w-full">
        <div class="flex flex-col sm:flex-row sm:items-end gap-4">
          <div>
            <label class="block text-sm font-medium text-agentium_gray-text mb-1" for="fromDate">
              From Date
            </label>
            <input
              id="fromDate"
              type="date"
              [(ngModel)]="fromDate"
              [max]="maxDate"
              class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-agentium_gray-text mb-1" for="toDate">
              To Date
            </label>
            <input
              id="toDate"
              type="date"
              [(ngModel)]="toDate"
              [max]="maxDate"
              class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-agentium_gray-text mb-1" for="limit">
              Limit <span class="text-gray-400 font-normal">(test only)</span>
            </label>
            <input
              id="limit"
              type="number"
              min="1"
              placeholder="No limit"
              [(ngModel)]="limit"
              class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full sm:w-32 focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            />
          </div>

          <button
            class="bg-momentum-deep_purple text-white px-4 py-2 rounded-full flex items-center justify-center gap-2 hover:bg-momentum-royal_purple transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            [disabled]="campaignDisabled || !fromDate || !toDate || !!dateRangeError || isPreviewing || isSending"
            (click)="preview()"
          >
            @if (isPreviewing) {
              <fa-icon [icon]="faSpinner" class="animate-spin"></fa-icon>
            } @else {
              <fa-icon [icon]="faEye"></fa-icon>
            }
            Preview
          </button>
        </div>
        @if (dateRangeError) {
          <div class="mt-2 text-sm text-red-600">{{ dateRangeError }}</div>
        }

        <!-- Category / LOB filters -->
        <div class="flex flex-col sm:flex-row sm:items-end gap-4 mt-4 pt-4 border-t border-gray-100">
          <div>
            <label class="block text-sm font-medium text-agentium_gray-text mb-1" for="category">
              Category <span class="text-gray-400 font-normal">(optional)</span>
            </label>
            <select
              id="category"
              [(ngModel)]="selectedCategory"
              class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            >
              <option value="">All Categories</option>
              @for (option of categoryOptions; track option.value) {
                <option [value]="option.value">{{ option.label }}</option>
              }
            </select>
          </div>

          <div>
            <div class="flex items-center justify-between mb-1">
              <label class="block text-sm font-medium text-agentium_gray-text" for="lobCodes">
                LOB <span class="text-gray-400 font-normal">(optional, multi-select)</span>
              </label>
              @if (selectedLobCodes.length > 0) {
                <button
                  type="button"
                  class="text-xs text-momentum-deep_purple hover:underline"
                  (click)="selectedLobCodes = []"
                >
                  Clear ({{ selectedLobCodes.length }})
                </button>
              }
            </div>
            <select
              id="lobCodes"
              multiple
              [(ngModel)]="selectedLobCodes"
              class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full sm:w-80 h-32 focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            >
              @for (option of lobOptions; track option.code) {
                <option [value]="option.code">{{ option.name }}</option>
              }
            </select>
          </div>
        </div>
      </div>

      <!-- Preview result -->
      @if (previewResult) {
        <div class="rounded-lg border border-gray-200 bg-white p-5 mb-4 w-full sm:w-fit">
          <div class="flex items-center gap-3 mb-1">
            <fa-icon [icon]="faCommentSms" class="text-momentum-deep_purple" size="lg"></fa-icon>
            <span class="text-agentium_gray-text">
              <strong class="text-2xl">{{ previewResult.eligibleCount }}</strong>
              referral(s) created
              @if (fromDate === toDate) {
                on {{ fromDate | date: "mediumDate" }}
              } @else {
                {{ fromDate | date: "mediumDate" }} through {{ toDate | date: "mediumDate" }}
              }
              would receive a text.
            </span>
          </div>
          @if (previewResult.skipped > 0) {
            <div class="mt-2 text-xs text-gray-500">
              {{ previewResult.skipped }} skipped —
              {{ previewResult.skippedInvalidPhone }} invalid phone,
              {{ previewResult.skippedDuplicatePhone }} duplicate phone,
              {{ previewResult.skippedOptedOut }} opted out,
              {{ previewResult.skippedRecentlyConverted }} recently converted,
              {{ previewResult.skippedNotWhitelisted }} not whitelisted
            </div>
          }
          <div class="mt-4">
            <button
              class="bg-momentum-deep_purple text-white px-4 py-2 rounded-full flex items-center justify-center gap-2 hover:bg-momentum-royal_purple transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              [disabled]="previewResult.eligibleCount === 0 || isSending"
              (click)="confirmSend()"
            >
              @if (isSending) {
                <fa-icon [icon]="faSpinner" class="animate-spin"></fa-icon>
              } @else {
                <fa-icon [icon]="faPaperPlane"></fa-icon>
              }
              Send to {{ previewResult.eligibleCount }} referral(s)
            </button>
          </div>
        </div>
      }

      <!-- Send result -->
      @if (sendResult) {
        <div class="rounded-lg border border-green-200 bg-green-50 p-5 w-full sm:w-fit">
          <div class="text-agentium_gray-text font-semibold mb-2">Campaign complete</div>
          <div class="flex gap-6 text-sm">
            <span class="text-green-700"><strong>{{ sendResult.sent }}</strong> sent</span>
            <span class="text-red-700"><strong>{{ sendResult.failed }}</strong> failed</span>
            <span class="text-gray-600"><strong>{{ sendResult.skipped }}</strong> skipped</span>
          </div>
          @if (sendResult.skipped > 0) {
            <div class="mt-2 text-xs text-gray-500">
              {{ sendResult.skippedInvalidPhone }} invalid phone,
              {{ sendResult.skippedDuplicatePhone }} duplicate phone,
              {{ sendResult.skippedOptedOut }} opted out,
              {{ sendResult.skippedRecentlyConverted }} recently converted,
              {{ sendResult.skippedNotWhitelisted }} not whitelisted
            </div>
          }
        </div>
      }

    </div>
  </div>
</div>
